import { useState, useMemo } from 'react';
import { Box, Typography, TextField, Chip, InputAdornment } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import { Link, useLocation, useNavigate } from '@tanstack/react-router';
import { Section } from '../ui/Section';
import { ResourceCard } from '../ui/ResourceCard';
import { getAllResources, ResourceType } from '../../lib/resources';
import { usePostHog } from '../widgets/PostHogProvider';

const TYPE_FILTERS = [
  { value: 'all' as const, label: 'All', color: '#334155', icon: undefined },
  { value: 'blog' as const, label: 'Blog', color: '#3b82f6', icon: <ArticleOutlinedIcon sx={{ fontSize: 16 }} /> },
  { value: 'research' as const, label: 'Research', color: '#8b5cf6', icon: <ScienceOutlinedIcon sx={{ fontSize: 16 }} /> },
  { value: 'case-study' as const, label: 'Case Studies', color: '#10b981', icon: <DescriptionOutlinedIcon sx={{ fontSize: 16 }} /> },
];

const VALID_TYPES = new Set(TYPE_FILTERS.map((f) => f.value));

export type TypeFilter = 'all' | ResourceType;

const allResources = getAllResources();

interface ResourceHubProps {
  defaultType?: TypeFilter;
}

export function ResourceHub({ defaultType = 'all' }: ResourceHubProps) {
  const location = useLocation();
  const isHubRoute = location.pathname === '/resources' || location.pathname === '/resources/';
  const searchParams = isHubRoute ? (location.search as Record<string, unknown>) : {};
  const navigate = useNavigate();

  const [localType, setLocalType] = useState<TypeFilter>(defaultType);
  const [localQuery, setLocalQuery] = useState('');

  const rawType = searchParams.type as string | undefined;
  const activeType = isHubRoute
    ? (VALID_TYPES.has(rawType as TypeFilter) ? (rawType as TypeFilter) : 'all')
    : localType;
  const searchQuery = isHubRoute ? ((searchParams.q as string) ?? '') : localQuery;
  const { capture } = usePostHog();

  const filteredResources = useMemo(() => {
    let results = allResources;

    if (activeType !== 'all') {
      results = results.filter((r) => r.type === activeType);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      results = results.filter(
        (r) =>
          r.title.toLowerCase().includes(q) ||
          r.description.toLowerCase().includes(q) ||
          r.tags?.some((t) => t.toLowerCase().includes(q)),
      );
    }

    return results;
  }, [activeType, searchQuery]);

  const updateFilters = (type: TypeFilter, q: string) => {
    if (isHubRoute) {
      navigate({ to: '/resources', search: { type: type === 'all' ? undefined : type, q: q || undefined }, replace: true });
    } else {
      setLocalType(type);
      setLocalQuery(q);
    }
  };

  const setActiveType = (newType: TypeFilter) => {
    capture('resources_filter_changed', { filter_type: newType, previous_type: activeType });
    updateFilters(newType, searchQuery);
  };

  const setSearchQueryValue = (q: string) => {
    updateFilters(activeType, q);
  };

  return (
    <>
      {/* Hero with integrated filters */}
      <Box
        sx={{
          pt: { xs: 4, md: 5 },
          pb: { xs: 3, md: 4 },
          background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.06) 0%, rgba(139, 92, 246, 0.06) 50%, rgba(16, 185, 129, 0.04) 100%)',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 2, sm: 3, md: 4 } }}>
          <Typography variant="h2" sx={{ fontWeight: 700, mb: 3, color: 'text.primary', textAlign: 'center' }}>
            Resources
          </Typography>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              flexWrap: 'wrap',
              justifyContent: 'center',
            }}
          >
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {TYPE_FILTERS.map((filter) => {
                const isActive = activeType === filter.value;
                return (
                  <Chip
                    key={filter.value}
                    icon={filter.icon as React.ReactElement | undefined}
                    label={filter.label}
                    onClick={() => setActiveType(filter.value)}
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      height: 40,
                      bgcolor: isActive ? filter.color : 'rgba(255,255,255,0.8)',
                      color: isActive ? '#fff' : 'text.secondary',
                      border: '1.5px solid',
                      borderColor: isActive ? filter.color : 'divider',
                      '& .MuiChip-icon': { color: isActive ? '#fff' : filter.color },
                      '&:hover': {
                        bgcolor: isActive ? filter.color : 'rgba(255,255,255,1)',
                        borderColor: isActive ? filter.color : 'grey.400',
                      },
                    }}
                  />
                );
              })}
            </Box>

            <TextField
              placeholder="Search resources..."
              value={searchQuery}
              onChange={(e) => setSearchQueryValue(e.target.value)}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                minWidth: 220,
                maxWidth: 320,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '20px',
                  fontSize: '0.875rem',
                  bgcolor: 'rgba(255,255,255,0.8)',
                },
              }}
            />

            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
              {filteredResources.length} resource{filteredResources.length !== 1 ? 's' : ''}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Section>

        {/* Card grid */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
            },
            gap: 3,
          }}
        >
          {filteredResources.map((resource) => (
            <Link
              key={`${resource.type}-${resource.id}`}
              to={resource.link}
              style={{ textDecoration: 'none', display: 'block' }}
              onClick={() =>
                capture('resource_clicked', {
                  resource_type: resource.type,
                  post_slug: resource.id,
                  post_title: resource.title,
                })
              }
            >
              <ResourceCard resource={resource} />
            </Link>
          ))}
        </Box>

        {filteredResources.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No resources found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Try adjusting your search or filters.
            </Typography>
          </Box>
        )}
      </Section>
    </>
  );
}
